import { useState, useCallback } from 'react';
import { Check, Droplets, FlaskConical } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { VolumetricFlask, Pipette, Beaker } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Selecting the Vinegar Sample',
    content: 'Commercial vinegar typically contains 4-8% acetic acid (CH3COOH) by mass. White distilled vinegar is commonly about 5% acetic acid. The exact concentration will be determined through titration with a standardized NaOH solution.',
    formulas: ['CH3COOH (acetic acid)', '% w/v = (mass solute / volume solution) x 100']
  },
  2: {
    title: 'Volumetric Pipette Technique',
    content: 'A volumetric pipette delivers a precise, fixed volume. Always rinse the pipette with the solution being measured before use. Draw liquid above the mark, then carefully drain to the mark while touching the tip to the flask wall. Transfer completely by touching the tip to the receiving vessel.',
    formulas: ['V = 10.00 mL (Class A tolerance: +/- 0.02 mL)']
  },
  3: {
    title: 'Dilution to Volume',
    content: 'Adding freshly boiled and cooled deionised water to the volumetric flask dilutes the vinegar sample. Boiling removes dissolved CO₂ which could affect the titration. Always add solvent slowly and stop just below the mark, then use a dropper for final adjustment. The meniscus bottom should touch the calibration line at eye level.',
    formulas: ['C1V1 = C2V2', 'Dilution Factor = V2/V1 = 100/10 = 10x']
  },
  4: {
    title: 'Mixing for Homogeneity',
    content: 'Inverting the flask multiple times ensures complete mixing of the solution. Without proper mixing, concentration gradients can lead to inaccurate aliquots. Stopper the flask securely before inverting. Mix at least 10 times for thorough homogenization.',
    formulas: ['Final concentration = Initial concentration / 10']
  }
};

export default function Part1VinegarPrep() {
  const [sampleSelected, setSampleSelected] = useState(false);
  const [pipetteUsed, setPipetteUsed] = useState(false);
  const [diluted, setDiluted] = useState(false);
  const [mixed, setMixed] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [sampleSelected, pipetteUsed, diluted, mixed].filter(Boolean).length;

  const handleSelectSample = useCallback(() => {
    if (!sampleSelected) {
      setSampleSelected(true);
      setCurrentStep(2);
    }
  }, [sampleSelected]);

  const handlePipette = useCallback(() => {
    if (sampleSelected && !pipetteUsed) {
      setPipetteUsed(true);
      setCurrentStep(3);
    }
  }, [sampleSelected, pipetteUsed]);

  const handleDilute = useCallback(() => {
    if (pipetteUsed && !diluted) {
      setDiluted(true);
      setCurrentStep(4);
    }
  }, [pipetteUsed, diluted]);

  const handleMix = useCallback(() => {
    if (diluted && !mixed) {
      setMixed(true);
    }
  }, [diluted, mixed]);

  const getFlaskFillLevel = () => {
    if (mixed) return 95;
    if (diluted) return 95;
    if (pipetteUsed) return 10;
    return 0;
  };

  const getFlaskColor = () => {
    if (mixed) return '#FFF8DC';
    if (pipetteUsed) return '#FFFACD';
    return '#E3F2FD';
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part I: Vinegar Preparation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Select Sample', hint: 'Obtain commercial vinegar', done: sampleSelected },
            { id: 2, title: 'Pipette Vinegar', hint: 'Transfer 10.00 mL to flask', done: pipetteUsed },
            { id: 3, title: 'Dilute to Volume', hint: 'Add water to 100 mL mark', done: diluted },
            { id: 4, title: 'Mix Thoroughly', hint: 'Invert flask several times', done: mixed }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="Vinegar Sample Preparation"
        completedCount={completedSteps}
        totalSteps={4}
        equipment={['Volumetric pipette', 'Volumetric flask', 'Vinegar sample']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="prep-workspace">
          <div className="equipment-row">
            {/* Vinegar Sample Beaker */}
            <div
              className={`equipment-station ${currentStep === 1 && !sampleSelected ? 'clickable' : ''}`}
              onClick={handleSelectSample}
              style={{ cursor: currentStep === 1 && !sampleSelected ? 'pointer' : 'default' }}
            >
              <Beaker
                size={250}
                fillLevel={sampleSelected ? 70 : 80}
                fillColor="#F5DEB3"
                label="Vinegar"
                active={currentStep === 1 && !sampleSelected}
              />
              <span className="equipment-label">Commercial Vinegar</span>
            </div>

            {/* Volumetric Pipette */}
            <div
              className={`equipment-station ${currentStep === 2 && !pipetteUsed ? 'clickable' : ''}`}
              onClick={handlePipette}
              style={{ cursor: currentStep === 2 && !pipetteUsed ? 'pointer' : 'default' }}
            >
              <Pipette
                volume={10}
                fillLevel={pipetteUsed ? 0 : sampleSelected ? 100 : 0}
                fillColor="#F5DEB3"
                active={currentStep === 2 && !pipetteUsed}
              />
              <span className="equipment-label">10 mL Volumetric Pipette</span>
            </div>

            {/* Volumetric Flask */}
            <div
              className={`equipment-station ${(currentStep === 3 || currentStep === 4) && !mixed ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 3) handleDilute();
                else if (currentStep === 4) handleMix();
              }}
              style={{ cursor: (currentStep === 3 || currentStep === 4) && !mixed ? 'pointer' : 'default' }}
            >
              <VolumetricFlask
                size={100}
                fillLevel={getFlaskFillLevel()}
                fillColor={getFlaskColor()}
                label="100 mL"
                active={(currentStep === 3 && !diluted) || (currentStep === 4 && !mixed)}
              />
              <span className="equipment-label">100 mL Volumetric Flask</span>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {sampleSelected && !pipetteUsed && (
              <div className="status-message info">
                <Droplets size={20} />
                Use the volumetric pipette to transfer exactly 10.00 mL of vinegar
              </div>
            )}
            {pipetteUsed && !diluted && (
              <div className="status-message info">
                <Droplets size={20} />
                Add freshly boiled and cooled deionised water to the 100 mL mark
              </div>
            )}
            {diluted && !mixed && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Invert the flask several times to mix thoroughly
              </div>
            )}
            {mixed && (
              <div className="status-message success">
                <Check size={20} />
                Sample preparation complete! Dilution factor: 10x
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Dilution Factor</h4>
            <p>
              By diluting 10 mL of vinegar to 100 mL, we create a 10x dilution.
              This makes the titration easier to perform and more accurate.
              Remember to multiply your results by the dilution factor.
            </p>
          </div>
        </div>

        <style>{`
          .prep-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 12px;
            color: var(--text-secondary);
            text-align: center;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp9"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
